import { useNavigate } from "react-router-dom";
import { useUpdateSearchParams } from "@/Hooks/useUpdateSearchParams";
import { ReactNode, useMemo } from "react";
import { type Client, GetClientsRequest } from "@/Stub/V1";
import { url } from "@/Helpers/general";
import { Button } from "primereact/button";
import { DataTable, DataTablePassThroughOptions, DataTableStateEvent } from "primereact/datatable";
import { Column, ColumnBodyOptions } from "primereact/column";
import { useGetClients } from "@/Service/Api/V1/ApiHooks/Clients/useGetClients";
import useGetClientsFiltersQuery, { ClientFilterType } from "@/Hooks/useGetClientsFiltersQuery";

type TableColumnDefinition = {
    label: string
    sortable?: boolean
    field?: string
    sortField?: string
    headerTooltip?: string
    body?: ReactNode | ((data: Client, options: ColumnBodyOptions) => ReactNode)
};

const dataTablePtOptions: DataTablePassThroughOptions = {
    root: {
        className: 'datatable-base'
    }
};

export type ClientsDatableProps = {
    requestParams: GetClientsRequest
};

const ClientsDatable = ({ requestParams }: ClientsDatableProps) => {
    const navigate = useNavigate();
    const {
        pageQuery,
        pageSizeQuery
    } = useGetClientsFiltersQuery();
    const updateClientsFilterHandler = useUpdateSearchParams();


    const clientsRequest = useMemo<GetClientsRequest>(() => {
        return {
            ...requestParams,
            page: pageQuery ? parseInt(pageQuery) : 1,
            per_page: pageSizeQuery ? parseInt(pageSizeQuery) : 10,
        };
    }, [requestParams, pageQuery, pageSizeQuery]);

    const {
        data: usersData,
        isLoading: customersLoading
    } = useGetClients({
        requestParams: clientsRequest
    });



    const navigateToClient = (client: Client) => {
        navigate(url('client', { clientId: client.id }));
    };

    const actionsBodyTemplate = (client: Client) => {
        return (
            <div className="flex gap-2">
                <Button
                    onClick={() => navigateToClient(client)}
                    label='View'
                />
            </div>
        );
    };



    const tableColumns: TableColumnDefinition[] = [
        {
            label: 'Id',
            field: 'id',
            body: (client) => {
                return <>
                    {client.id ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'SSO Domain',
            field: 'sso_domain',
            body: (client) => {
                return <>
                    {client.sso_domain ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Name',
            field: 'name',
            body: (client) => {
                return <>
                    {client.name ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Actions',
            body: actionsBodyTemplate
        }
    ];


    const onPagination = (e: DataTableStateEvent) => {
        updateClientsFilterHandler(ClientFilterType.Page, ((e.first / e.rows) + 1).toString());
        updateClientsFilterHandler(ClientFilterType.PageSize, e.rows.toString());
    };

    const first = pageSizeQuery && pageQuery ? Number(pageSizeQuery) * Number(pageQuery) - 10 : 0;
    const rows = pageSizeQuery ? Number(pageSizeQuery) : 10;

    return (
        <div>
            <div className="content-container">
                <DataTable
                    lazy
                    loading={customersLoading}
                    emptyMessage="No clients found."
                    value={usersData?.data}
                    totalRecords={usersData?.meta.total}
                    paginator
                    rows={rows}
                    rowsPerPageOptions={[10, 20, 50]}
                    first={first}
                    pt={dataTablePtOptions}
                    onPage={e => {
                        onPagination(e);
                    }}
                >
                    {tableColumns.map((column, index) =>
                        <Column
                            key={`customers-column-${index}`}
                            field={column.field}
                            body={column.body}
                            header={column.label}
                            sortable={column.sortable}
                            sortField={column.sortField}
                            headerTooltip={column.headerTooltip}
                        />
                    )}
                </DataTable>
            </div>
        </div>
    );
};
export default ClientsDatable;